/** Avisos que nacen de un acto y mueren con él. El estado persistente que la ley exige ver vive en
 * `components/page-notices.tsx`; esto vive fuera de React porque lo dispara el `MutationCache`. */

/** Manda el ciclo de vida: un `exito` se cierra solo a los 6 s, un `error` espera a la persona. */
type AvisoTipo = 'error' | 'exito'

type AvisoAccion = {
  etiqueta: string
  alActivar: () => void
}

export type Aviso = {
  id: number
  tipo: AvisoTipo
  titulo: string
  detalle: string
  testId?: string
  accion?: AvisoAccion
}

export type OpcionesAviso = {
  tipo?: AvisoTipo
  testId?: string
  accion?: AvisoAccion
}

/** Un fallo no se descarta solo; al pasar el tope, el más viejo cede el puesto. */
const TOPE_VISIBLE = 3

let avisos: readonly Aviso[] = []
let siguienteId = 1
const escuchas = new Set<() => void>()

function emitir(siguientes: readonly Aviso[]): void {
  avisos = siguientes
  for (const escucha of escuchas) escucha()
}

export function publicarAviso(titulo: string, detalle: string, opciones: OpcionesAviso = {}): number {
  const id = siguienteId
  siguienteId += 1
  // La misma acción fallando dos veces es un solo problema: se refresca en vez de apilar.
  const sinRepetido = avisos.filter((aviso) => !(aviso.titulo === titulo && aviso.detalle === detalle))
  const nuevo: Aviso = {
    id,
    tipo: opciones.tipo ?? 'error',
    titulo,
    detalle,
    testId: opciones.testId,
    accion: opciones.accion,
  }
  emitir([...sinRepetido, nuevo].slice(-TOPE_VISIBLE))
  return id
}

export function publicarExito(titulo: string, detalle: string, opciones: Omit<OpcionesAviso, 'tipo'> = {}): number {
  return publicarAviso(titulo, detalle, {...opciones, tipo: 'exito'})
}

export function descartarAviso(id: number): void {
  const siguientes = avisos.filter((aviso) => aviso.id !== id)
  if (siguientes.length !== avisos.length) emitir(siguientes)
}

export function suscribirAvisos(escucha: () => void): () => void {
  escuchas.add(escucha)
  return () => {
    escuchas.delete(escucha)
  }
}

/** Referencia estable mientras nada cambie — `useSyncExternalStore` la compara por
 * identidad y volvería a renderizar sin fin si acá se armara un arreglo nuevo. */
export function leerAvisos(): readonly Aviso[] {
  return avisos
}
